import { API } from '@/api'
import { getOrders } from '@/api/orders.api'
import type { Order, OrderStatus, WorkloadBucket } from '@/common/order.common'

/** One service of GET /orders/workload: the shared contract (SIB-1107). */
export type WorkloadBucketDto = WorkloadBucket

export type WorkloadDto = {
  userId: string
  from: string
  to: string
  buckets: WorkloadBucketDto[]
}

/** The logged-in SIB's workload for a window inside one calendar year, optionally per recipient. */
export function getSibWorkload(params: {
  userId: string
  from: string
  to: string
  customerId?: number
}): Promise<WorkloadDto> {
  const query = new URLSearchParams({ userId: params.userId, from: params.from, to: params.to })
  if (params.customerId != null) query.append('customerId', String(params.customerId))
  return API<WorkloadDto>(
    `/orders/workload?${query.toString()}`,
    { headers: { 'x-disable-cache': '1' } },
    'Fehler beim Laden der Auslastung',
    'WORKLOAD_FETCH_ERROR'
  )
}

/** The order statuses the backend counts as ordered (workload, capacity, invoice summary). */
export const COUNTED_ORDER_STATUSES: OrderStatus[] = ['APPROVED', 'COMPLETED']

const PAGE_SIZE = 100

/**
 * Every APPROVED or COMPLETED order of the logged-in SIB for an order year, across all pages. The
 * server pages GET /orders, so totals from the first page alone would be incomplete.
 */
export async function loadCountedOrders(year: number): Promise<Order[]> {
  const byId = new Map<number, Order>()
  for (const status of COUNTED_ORDER_STATUSES) {
    let fetched = 0
    for (let page = 1; ; page++) {
      const res = await getOrders({ status, year, page, pageSize: PAGE_SIZE })
      const batch = Object.values(res.items ?? {}).flatMap(bySib => Object.values(bySib).flat())
      const known = byId.size
      batch.forEach(o => byId.set(o.id, o))
      fetched += batch.length
      // The server may cap the page below PAGE_SIZE, so a short page proves nothing. Follow totalItems
      // when it is reported; without it, keep paging until a page adds no new order (empty or
      // echoed), which also ends the loop for a server that ignores the page parameter.
      const total = (res as { totalItems?: number }).totalItems
      const done = byId.size === known || (total != null && fetched >= total)
      if (done) break
    }
  }
  return [...byId.values()]
}
